import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import { HEARINGTYPE_INCLUDE } from '../../constants/api';
import {
  createHearingTypeInitialize,
  createHearingTypeSuccess,
  deleteHearingTypeInitialize,
  deleteHearingTypeSuccess,
  getAllHearingsSuccess,
  getAllHearingTypesError,
  getAllHearingTypesInitialize,
  getAllHearingTypesSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
  updateHearingTypeInitialize,
  updateHearingTypeSuccess,
} from '../actions/api';
import { HearingTypeConnector } from '../connectors/hearingTypeConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  generateCreateApiCall,
  generateGetAllApiCall,
  generateUpdateApiCall,
} from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getInitActionPayload,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import {
  allHearingsSelector,
  hearingByIdSelector,
  hearingsByIdsSelector,
} from './hearingSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /hearingType
//  - POST /hearingType
//  - PATCH /hearingType/{id}
//  - DELETE /hearingType/{id}
//
// HearingType is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{hearingId}

// -------- Reducer --------
const hearingTypeReducer = createReducer(
  {
    all: [],
    isFetchingHearingTypes: false,
  },
  (builder) => {
    builder
      .addCase(getAllHearingTypesInitialize, (state) => {
        state.isFetchingHearingTypes = true;
      })
      .addCase(getAllHearingTypesError, (state) => {
        state.isFetchingHearingTypes = false;
      })
      .addCase(getAllHearingTypesSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingHearingTypes: false,
        });
        const hearingTypes = getRequestData(action);
        return updateDtoList(newState, 'all', hearingTypes);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingTypes = data.filter(
          (include) => include.type === HEARINGTYPE_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingTypes);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingTypes = data.filter(
          (include) => include.type === HEARINGTYPE_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingTypes);
      })
      .addCase(updateHearingTypeSuccess, (state, action) => {
        const hearingType = getRequestData(action);
        return updateDtoList(state, 'all', hearingType);
      })
      .addCase(deleteHearingTypeSuccess, (state, action) => {
        const hearingTypeId = getInitActionPayload(action);
        return removeItemFromList(state, 'all', hearingTypeId);
      })
      .addCase(createHearingTypeSuccess, (state, action) => {
        const hearingType = getRequestData(action);
        return updateDtoList(state, 'all', hearingType);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingTypes = data.filter(
          (include) => include.type === HEARINGTYPE_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingTypes);
      });
  },
);

// -------- Sagas ----------
export function* hearingTypeSaga() {
  yield all([
    fork(getAllHearingTypesSaga),
    fork(updateHearingTypeSaga),
    fork(deleteHearingTypeSaga),
    fork(createHearingTypeSaga),
  ]);
}

function* getAllHearingTypesSaga() {
  yield takeEvery(
    getAllHearingTypesInitialize.type,
    generateGetAllApiCall(HearingTypeConnector, function* (connector) {
      return yield call([connector, connector.getHearingTypes]);
    }),
  );
}

function* updateHearingTypeSaga() {
  yield takeEvery(
    updateHearingTypeInitialize.type,
    generateUpdateApiCall(HearingTypeConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateHearingType],
        action.payload.hearingTypeId,
        requestData,
      );
    }),
  );
}

function* deleteHearingTypeSaga() {
  yield takeEvery(
    deleteHearingTypeInitialize.type,
    generateUpdateApiCall(HearingTypeConnector, function* (connector, action) {
      return yield call(
        [connector, connector.deleteHearingType],
        action.payload,
      );
    }),
  );
}

function* createHearingTypeSaga() {
  yield takeEvery(
    createHearingTypeInitialize.type,
    generateCreateApiCall(HearingTypeConnector, function* (connector, action) {
      return yield call(
        [connector, connector.createHearingType],
        action.payload,
      );
    }),
  );
}

// -------- Selectors --------
const selectGetAllHearingTypes = (state) =>
  selectAllFromApiState(state, 'hearingType');
const allHearingTypesSelector = createSelector(
  selectGetAllHearingTypes,
  (hearingTypes) => hearingTypes,
);
const allHearingTypesWithNumberOfHearingsSelector = createSelector(
  [allHearingTypesSelector, allHearingsSelector],
  (hearingTypes, hearings) => {
    const result = [];
    hearingTypes.forEach((hearingType) => {
      let amount = 0;

      hearings.forEach((hearing) => {
        if (getRelId(hearing, 'hearingType') === getEntityId(hearingType)) {
          amount += 1;
        }
      });

      const newHearingType = Object.assign({}, hearingType, {
        attributes: { ...hearingType.attributes, numOfHearings: amount },
        relationships: { ...hearingType.relationships },
      });
      result.push(newHearingType);
    });
    return result;
  },
);
const hearingTypeByIdSelector = (hearingTypeId) =>
  createSelector(allHearingTypesSelector, (hearingTypes) =>
    hearingTypes.find((h) => getEntityId(h) === hearingTypeId),
  );
const numberOfHearingsOnHearingTypeByIdSelector = (hearingTypeId) =>
  createSelector(allHearingsSelector, (hearings) => {
    if (!hearingTypeId) {
      return null;
    }
    var result = 0;
    hearings.forEach((hearing) => {
      if (getRelId(hearing, 'hearingType') === hearingTypeId) {
        result += 1;
      }
    });
    return result;
  });
const hearingTypeByHearingIdSelector = (hearingId) =>
  createSelector(
    [allHearingTypesSelector, hearingByIdSelector(hearingId)],
    (hearingTypes, hearing) =>
      hearingTypes.find(
        (s) => getEntityId(s) === getRelId(hearing, 'hearingType'),
      ),
  );
const hearingTypesByHearingIdsSelector = (hearingIds) =>
  createSelector(
    [allHearingTypesSelector, hearingsByIdsSelector(hearingIds)],
    (hearingTypes, hearings) =>
      hearingTypes.filter((hearingType) =>
        hearings.some(
          (hearing) =>
            getRelId(hearing, 'hearingType') === getEntityId(hearingType),
        ),
      ),
  );

const hearingTypeState = (state) => state.api.hearingType;

const isFetchingHearingTypesSelector = createSelector(
  hearingTypeState,
  (state) => state.isFetchingHearingTypes,
);

// -------- Exporting --------
export {
  allHearingTypesSelector,
  hearingTypeByIdSelector,
  hearingTypesByHearingIdsSelector,
  hearingTypeByHearingIdSelector,
  allHearingTypesWithNumberOfHearingsSelector,
  numberOfHearingsOnHearingTypeByIdSelector,
  isFetchingHearingTypesSelector,
};
export default hearingTypeReducer;
